import { Badge, Center, Image, Loader } from '@hover/blueprint';
import type { AiGeneratedImage } from 'mock/generated/types';
import React from 'react';
import { useHistory } from 'react-router';

import { useCustomization } from 'src/modules/homeownerApp/components/Customize/useCustomization';
import { useFeaturedDisplayImages } from 'src/modules/homeownerApp/components/Embed/hooks/useFeaturedDisplayImages';
import { PeekingCarousel } from 'src/modules/homeownerApp/components/PeekingCarousel';
import {
  useAltTextForImage,
  useGeneratedImageLabel,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { Route, generatePath } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';

import { usePartnerCobranding } from '../../hooks/usePartner';

const CustomizeDesignIdeasGalleryImage = ({
  image,
}: {
  image: AiGeneratedImage;
}) => {
  const altText = useAltTextForImage(image);
  const imageLabel = useGeneratedImageLabel(image);
  const { id, generatedImagePath } = image;
  const cobranding = usePartnerCobranding();

  return (
    <Center
      height="calc(100dvw - 80px)"
      maxHeight={cobranding ? '255px' : '280px'}
      maxWidth={cobranding ? '255px' : '280px'}
      position="relative"
      width="calc(100dvw - 80px)"
    >
      <Image
        alt={altText}
        aspectRatio={1}
        borderRadius={400}
        cursor="pointer"
        draggable
        height="100%"
        key={id}
        maxHeight="100%"
        maxWidth="100%"
        objectFit="cover"
        src={generatedImagePath}
      />
      <Badge
        colorMode="dark"
        left={400}
        position="absolute"
        top={400}
        variant="glass"
      >
        {imageLabel}
      </Badge>
    </Center>
  );
};

export const CustomizeDesignIdeasGallery = () => {
  const history = useHistory();
  const { targetImage } = useCustomization();
  const { trackingData } = useHomeownerAppContext();

  const { featuredDisplayImages, loading } = useFeaturedDisplayImages(
    targetImage.aiOriginalImageId,
  );

  const updateRoute = (index: number) => {
    const currentDisplayImage = featuredDisplayImages?.[index];

    if (currentDisplayImage) {
      buttonPressed({
        ...trackingData,
        button_text: currentDisplayImage.style || 'Unknown',
        style: currentDisplayImage.style || undefined,
        space_type: currentDisplayImage.spaceType || undefined,
        track_id: 'sample-design-style-ideas',
      });

      history.push(
        generatePath(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path, {
          aiDisplayImageId: currentDisplayImage.id,
        }),
      );
    }
  };

  if (loading || !featuredDisplayImages) {
    return (
      <Center height="100%" w="100dvw">
        <Loader color="white" size="huge" />
      </Center>
    );
  }

  return (
    <Center height="100%" justifyContent="start" w="100dvw">
      <PeekingCarousel height="100%" setCurrentIndex={updateRoute}>
        {featuredDisplayImages.map(aiDisplayImage => (
          <CustomizeDesignIdeasGalleryImage
            image={aiDisplayImage.aiGeneratedImage}
          />
        ))}
      </PeekingCarousel>
    </Center>
  );
};
